/*
  Split test `atc-upsell-popup`, variant routing.

  The post-ATC sheet is not rendered into the page. `product-addons.js` fetches
  `?view=addons` and injects the result into #addons-drawer-mount long after the
  Simple Split Testing embed has finished its pass, and its parseAndMount keeps
  only the <product-addons-drawer> element, so neither the embed nor a <template>
  sibling can reach the mounted markup. Assignment is therefore read here, from
  window.splt, and applied by routing the open call.

  The trigger itself is untouched: global.js still calls
  window.addonsDrawerLoader.openForQuickAdd from the PDP add-to-cart path. This
  file only decides which sheet that call opens, so both variants are entered the
  same way and the test measures the sheet rather than the way in.
*/
(function () {
  'use strict';

  var TEST_ID = 'atc-upsell-popup';
  var VARIANT = 'variant-a';

  /* ------------------------------------------------------------- reporting */

  /*
    One reporter for the whole post-ATC popup: control, variant-a, and the split
    embed that decides between them. Anything that stops a shopper seeing a
    popup they should have seen ends up here.

    Rows carry kind: 'upsell_popup' and a stage, so one query finds them all:

      logs query "SELECT time, message, extra FROM heltra_storefront_logs
                  WHERE event_name = 'upsell_popup_error' ORDER BY time DESC LIMIT 50"

    The name has to exist in KNOWN_EVENTS in the logs Worker or the row is
    dropped rather than stored. It was added and deployed on 2026-08-18.
  */
  var EVENT_NAME = 'upsell_popup_error';
  var reported = {};

  function report(stage, err, extra) {
    try {
      // Once per stage per page. A drawer that fails on every interaction should
      // not turn one broken session into a hundred rows.
      if (reported[stage]) return;
      reported[stage] = true;

      if (!window.slog) return;

      var payload = {
        kind: 'upsell_popup',
        stage: stage,
        test: TEST_ID,
        variant: assignedVariant() || 'unresolved',
        splt: window.splt ? 'present' : 'missing',
        handle: (document.querySelector('product-component[data-handle]') || { getAttribute: function () { return ''; } }).getAttribute('data-handle') || '',
      };
      if (err) payload.error = String((err && err.message) || err).slice(0, 300);
      if (extra) {
        Object.keys(extra).forEach(function (key) {
          payload[key] = extra[key];
        });
      }

      window.slog(EVENT_NAME, { message: 'upsell_popup: ' + stage, extra: payload });
    } catch (e) {
      /* reporting must never be the thing that breaks the page */
    }
  }

  // Shared with the variant-a controller and the control instrumentation, so all
  // three report the same shape through the same throttle.
  window.wbUpsellLog = report;

  /*
    Resolving the assignment.

    window.splt.getVariant() compares its argument against splitTestId only, so
    it returns null for the handle even though the whole markup contract is
    keyed on the handle through data-split-id. Reading the assignment list
    directly matches on either, so this keeps working whichever the app settles
    on. Filed against the app as its own bug.
  */
  function assignedVariant() {
    try {
      var list = (window.splt && window.splt.assignments) || [];
      for (var i = 0; i < list.length; i += 1) {
        var entry = list[i];
        if (!entry || !entry.variant) continue;
        if (entry.splitTestHandle === TEST_ID || entry.splitTestId === TEST_ID) return entry.variant.handle;
      }
      if (window.splt && typeof window.splt.getVariant === 'function') return window.splt.getVariant(TEST_ID);
      return null;
    } catch (err) {
      return null;
    }
  }

  /*
    The test is over and variant-a shipped to everyone (2026-09-03).

    This used to read the assignment. It no longer can: once the split test
    stops, splt publishes no assignment for it, so reading one would send every
    visitor back to control's drawer. assignedVariant() is kept because the
    logs reporter still records what the embed says, which is how a stray
    assignment would show up if the test were ever restarted.
  */
  function isVariantA() {
    return true;
  }

  /*
    Whether the embed has finished deciding.

    The assignment arrives late on a first visit: the embed posts to its proxy
    and publishes when the response lands, and until then splt.assignments is
    empty. Reading it at that moment says "control", which is not an answer,
    it is the absence of one. A visitor the test later records as variant-a
    then got control's drawer for that add, and published nothing, which the
    results read as a sheet that never opened. Slow connections make this
    window wider, which is why it showed up on mobile and nowhere else.

    No embed at all is settled: there is nothing to wait for and control is
    correct. A populated list is settled whichever way it was populated.
  */
  /*
    Nothing left to settle, same reason. Left in place rather than deleted
    because waitForAssignment and the add-to-cart path both call it, and a
    constant true keeps that path from stalling 4s on an assignment that is
    never coming.
  */
  function assignmentSettled() {
    return true;
  }

  // Resolves once the embed publishes, or after maxMs, whichever is first.
  // onAssignments is the embed's own API for this and fires synchronously if
  // the list is already there; polling covers an embed build that lacks it.
  function waitForAssignment(maxMs) {
    if (assignmentSettled()) return Promise.resolve({ resolved: true, ms: 0 });
    var started = Date.now();
    return new Promise(function (resolve) {
      var done = false;
      var finish = function (resolved) {
        if (done) return;
        done = true;
        window.clearTimeout(timer);
        window.clearInterval(poll);
        resolve({ resolved: resolved, ms: Date.now() - started });
      };
      var timer = window.setTimeout(function () {
        finish(assignmentSettled());
      }, maxMs);
      var poll = window.setInterval(function () {
        if (assignmentSettled()) finish(true);
      }, 50);
      try {
        if (window.splt && typeof window.splt.onAssignments === 'function') {
          window.splt.onAssignments(function () {
            finish(true);
          });
        }
      } catch (err) {
        /* the poll still ends this */
      }
    });
  }

  /* --------------------------------------------------- watching the embed */

  /*
    The split embed is an app block, so its failures are invisible from here
    unless we watch for them. Two are worth knowing about, and both have already
    happened on this store:

    the assignment call returning non-2xx, which is how an expired preview token
    presents (401 invalid_preview_token) and which silently sends every visitor
    to control; and the embed not being on the page at all, which looks identical
    from the shopper's side.

    fetch is wrapped only to observe. The original is called with the original
    arguments and its promise is returned untouched, and `this` is preserved,
    because a wrapper that forgets that breaks every bare fetch() on the page.
  */
  var PROXY_RE = /\/apps\/[^/]*split[^/]*\//i;

  try {
    var nativeFetch = window.fetch;
    if (typeof nativeFetch === 'function') {
      window.fetch = function (input, init) {
        var result = nativeFetch.apply(this, arguments);
        try {
          var url = typeof input === 'string' ? input : input && input.url;
          if (url && PROXY_RE.test(String(url))) {
            result.then(
              function (res) {
                if (res && !res.ok) {
                  report('splt_proxy_status', null, { status: res.status, url: String(url).split('?')[0] });
                }
              },
              function (err) {
                report('splt_proxy_failed', err, { url: String(url).split('?')[0] });
              }
            );
          }
        } catch (e) {
          /* observation only */
        }
        return result;
      };
    }
  } catch (err) {
    /* if fetch cannot be wrapped, everything else still works */
  }

  /*
    A failed load is forgotten, not remembered.

    This used to keep the rejected promise, so one asset that failed during the
    idle warm, which on a phone is usually the shopper leaving the page or a
    radio blip, made every later call fail too. The add to cart minutes later
    then reported variant_open_failed and went to control, with the asset
    perfectly fetchable the whole time. The dead element is removed so the
    retry gets a real request rather than the browser's memory of the failure.

    The rejection carries the url, because an error event stringifies as
    "[object Event]", which is what the logs held for every one of these.
  */
  function loadOnce(state, tag, attrs) {
    if (state.promise) return state.promise;
    state.promise = new Promise(function (resolve, reject) {
      var el = document.createElement(tag);
      Object.keys(attrs).forEach(function (key) {
        el.setAttribute(key, attrs[key]);
      });
      el.addEventListener('load', resolve, { once: true });
      el.addEventListener(
        'error',
        function () {
          state.promise = null;
          if (el.parentNode) el.parentNode.removeChild(el);
          reject(new Error(tag + ' load failed: ' + (attrs.src || attrs.href || '')));
        },
        { once: true }
      );
      (tag === 'link' ? document.head : document.body).appendChild(el);
    });
    return state.promise;
  }

  var cssState = {};
  var jsState = {};

  function assets() {
    var urls = window.wbUpsellV2Assets || {};
    if (!urls.css || !urls.js) return Promise.reject(new Error('wb-upsell-v2 asset urls missing'));
    return Promise.all([
      loadOnce(cssState, 'link', { rel: 'stylesheet', href: urls.css }),
      loadOnce(jsState, 'script', { src: urls.js, defer: 'defer' }),
    ]);
  }

  function openVariantA(handle, detail) {
    return assets()
      .then(function () {
        if (!window.wbUpsellV2 || typeof window.wbUpsellV2.open !== 'function') {
          throw new Error('wb-upsell-v2 controller missing');
        }
        return window.wbUpsellV2.open(handle, detail || {});
      })
      .catch(function (err) {
        report('variant_open_failed', err, { handle: handle || '' });
        // A broken variant must never cost an add to cart. Hand the call back to
        // control, which is still fully present and loaded.
        return null;
      });
  }

  // product-addons.min.js assigns window.addonsDrawerLoader when it finishes
  // running. Wrapping the property rather than the object means the wrap is in
  // place before the first possible call, whichever script order wins.
  function wrap(loader) {
    if (!loader || loader.__wbUpsellV2Wrapped) return loader;
    var original = loader.openForQuickAdd;
    if (typeof original !== 'function') return loader;

    loader.openForQuickAdd = function (handle, detail) {
      if (!isVariantA()) return original.call(loader, handle, detail);
      return openVariantA(handle, detail).then(function (opened) {
        if (opened) return true;
        return original.call(loader, handle, detail);
      });
    };
    loader.__wbUpsellV2Wrapped = true;
    return loader;
  }

  /*
    The other way in, which the loader wrap never covered.

    Control binds a document click for
    [data-action="open-addons-drawer"][data-product-handle] and opens itself
    straight from it. That is how the cart drawer's own "Want an add-on?" link
    works, and how the bundle page asks for tools, so both of them kept getting
    control's drawer long after every product page had moved to this one.

    Capture phase, so it runs before control's bubble-phase listener whatever
    order the two scripts loaded in, and stopPropagation keeps that listener
    from firing at all. If this sheet declines, the click is replayed so control
    still gets it: an add-on offer that opens nothing is worse than an old one.
  */
  document.addEventListener(
    'click',
    function (event) {
      if (!isVariantA()) return;
      if (!event.target || !event.target.closest) return;
      var trigger = event.target.closest('[data-action="open-addons-drawer"][data-product-handle]');
      if (!trigger || trigger.__wbReplayed) return;

      var handle = trigger.getAttribute('data-product-handle');
      if (!handle) return;

      event.preventDefault();
      event.stopPropagation();

      openVariantA(handle, {
        variant: Number(trigger.getAttribute('data-parent-variant-id')) || null,
        quantity: Number(trigger.getAttribute('data-parent-quantity')) || 1,
        parentKey: trigger.getAttribute('data-parent-key') || null,
        skipClosure: trigger.getAttribute('data-wb-skip-closure') === 'true',
      }).then(function (opened) {
        if (opened) return;
        // Hand it back to control, once, without looping through here again.
        trigger.__wbReplayed = true;
        trigger.click();
        trigger.__wbReplayed = false;
      });
    },
    true
  );

  var stored = window.addonsDrawerLoader;
  try {
    Object.defineProperty(window, 'addonsDrawerLoader', {
      configurable: true,
      get: function () {
        return stored;
      },
      set: function (value) {
        stored = wrap(value);
      },
    });
    if (stored) wrap(stored);
  } catch (err) {
    // Older engines that refuse the redefinition still get the wrap if the
    // loader is already there.
    if (stored) wrap(stored);
  }

  /*
    The PDP main add to cart does not go through the loader at all.

    global.js dispatches a cancelable `product:before-add`, and control's mounted
    drawer listens for it on document, calls preventDefault to cancel the add,
    and opens itself. That, not openForQuickAdd, is how the post-ATC popup
    actually opens on a product page; the loader path only covers quick add from
    product cards.

    So variant-a intercepts the same event, in the capture phase, which runs
    before control's listener regardless of which script registered first.
  */
  /*
    The plain add global.js would have performed. Used only when the popup was
    cancelled and neither arm took responsibility for it.
  */
  // Rescue adds in flight, keyed by variant and quantity.
  var addingDirect = Object.create(null);

  // Presses being decided, keyed by handle, variant and quantity.
  var deciding = Object.create(null);

  function addParentDirect(detail) {
    var variantId = Number(detail && detail.variant) || null;
    if (!variantId) return Promise.resolve();

    // Defence in depth. The interceptor above already refuses a second press
    // while the first is being decided, but this is the call that actually
    // posts to the cart, so it refuses to run twice for the same line on its
    // own account.
    var key = variantId + ':' + (Number(detail && detail.quantity) || 1);
    if (addingDirect[key]) {
      report('duplicate_rescue_suppressed', null, { variant: variantId });
      return Promise.resolve();
    }
    addingDirect[key] = true;

    var root = (window.routes && window.routes.localeRootUrl) || '';
    if (root.charAt(root.length - 1) === '/') root = root.slice(0, -1);

    return fetch(root + '/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      credentials: 'same-origin',
      body: JSON.stringify({
        items: [
          {
            id: variantId,
            quantity: Number(detail && detail.quantity) || 1,
            properties: (detail && detail.properties) || {},
          },
        ],
      }),
    })
      .then(function (res) {
        if (!res.ok) throw new Error('rescue add failed: ' + res.status);
        return res.json();
      })
      .then(function () {
        window._addonsDrawerActive = false;
        document.dispatchEvent(new CustomEvent('cart:updated'));
        document.dispatchEvent(new CustomEvent('product:added', { bubbles: true, detail: {} }));
      })
      .catch(function (err) {
        report('rescue_add_failed', err, { handle: productHandle() });

        /*
          Tell the shopper. This is the worst failure in the flow: the popup
          cancelled the add to cart and then failed to put it back, so without
          this they walk away believing they bought something.

          The theme already listens for cart:add-error on document and raises
          its own status toast, so this reuses that surface rather than
          inventing a second one.
        */
        try {
          document.dispatchEvent(
            new CustomEvent('cart:add-error', {
              bubbles: true,
              detail: { error: err },
            })
          );
        } catch (e) {
          /* reporting the failure must not become a second failure */
        }
      })
      .then(function () {
        delete addingDirect[key];
      }, function () {
        delete addingDirect[key];
      });
  }

  function productHandle() {
    var pdp = document.querySelector('product-component[data-handle]');
    return pdp ? pdp.getAttribute('data-handle') : '';
  }

  function currentVariantId() {
    var input = document.querySelector('product-component form[data-product-form] input[name="id"]');
    var id = Number(input && input.value);
    return Number.isFinite(id) && id ? id : null;
  }

  /*
    Warming.

    Control feels instant because product-addons.js prefetches its drawer on page
    load, so add to cart is only the slide. Without the same treatment variant-a
    paid for two round trips on the click, its assets and then its view, which is
    the delay that was showing up as roughly half a second.

    Warmed twice: once at idle after load, so it is ready long before anyone
    reaches the button, and again on first intent toward add to cart, because the
    free buckle is a per-variant metafield and the shopper may have changed
    colour since. A warm that has not finished costs nothing; open() falls back
    to fetching and reports open_cold so the gap is visible in the logs.
  */
  var warming = false;
  var rewarmWanted = false;
  var warmSubscribed = false;

  function warm() {
    /*
      An assignment that has not arrived yet is waited for, not read as
      control. The idle warm often ran before the embed's proxy call came back
      on a first visit, saw no assignment, and returned for good, so the sheet
      was never warmed and every add to cart on that page paid the cold path.
      Measured over 14 days: 629 of 680 cold opens had nothing warmed at all.
    */
    if (!assignmentSettled()) {
      if (warmSubscribed) return;
      warmSubscribed = true;
      waitForAssignment(4000).then(function () {
        warmSubscribed = false;
        warm();
      });
      return;
    }
    if (!isVariantA()) return;
    var handle = productHandle();
    if (!handle) return;
    if (warming) {
      // A change during a warm is not dropped: the finished warm is for the
      // variant that was current when it started, so it runs once more.
      rewarmWanted = true;
      return;
    }
    warming = true;

    assets()
      .then(function () {
        if (window.wbUpsellV2 && typeof window.wbUpsellV2.prefetch === 'function') {
          return window.wbUpsellV2.prefetch(handle, currentVariantId());
        }
      })
      .catch(function (err) {
        report('warm_failed', err, { handle: handle });
      })
      .then(function () {
        warming = false;
        if (rewarmWanted) {
          rewarmWanted = false;
          warm();
        }
      });
  }

  /*
    Warmed from DOMContentLoaded, not load.

    load waits for every image on the page, and on a phone a product gallery
    can hold that back for seconds, past the point where many shoppers have
    already pressed add to cart. Idle time before load is still idle time. The
    load listener stays as a second chance for a page whose idle callback was
    starved, since prefetch dedupes on its key and a repeat costs nothing.
  */
  function scheduleWarm() {
    if (typeof window.requestIdleCallback === 'function') window.requestIdleCallback(warm, { timeout: 2500 });
    else window.setTimeout(warm, 600);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleWarm, { once: true });
  } else {
    scheduleWarm();
  }
  if (document.readyState !== 'complete') window.addEventListener('load', scheduleWarm, { once: true });

  /*
    A variant change makes the warmed sheet the wrong one, since the free buckle
    is a per-variant metafield, so the new variant is warmed straight away rather
    than waiting for someone to reach for the button.

    Debounced, because a shopper flicking through colours fired one view fetch
    per change and Shopify answered a run of those with 429s, which is what
    most of the non-network prefetch failures in the logs were.
  */
  var changeWarmTimer = null;
  document.addEventListener('change', function (event) {
    var target = event.target;
    if (!target || !target.closest) return;
    if (!target.closest('product-component')) return;
    window.clearTimeout(changeWarmTimer);
    changeWarmTimer = window.setTimeout(warm, 300);
  });

  // Intent, the same signal the theme already uses to warm the add-ons script.
  ['pointerover', 'touchstart', 'focusin'].forEach(function (type) {
    document.addEventListener(
      type,
      function (event) {
        var target = event.target;
        if (!target || !target.closest) return;
        if (!target.closest('product-component form[data-product-form]')) return;
        warm();
      },
      { capture: true, passive: true }
    );
  });

  document.addEventListener(
    'product:before-add',
    function (event) {
      if (!isVariantA()) return;
      // Set on the event we re-dispatch when variant-a declines, so control gets
      // its turn without this handler catching it a second time.
      if (event.__wbUpsellV2Passthrough) return;

      var handle = productHandle();
      if (!handle) return;

      // Cancel the add and keep control's listener from also running. The parent
      // line is added later, either by the sheet's own CTA or, if the shopper
      // dismisses, by the same add-parent-only fallback control uses.
      event.preventDefault();
      event.stopImmediatePropagation();

      var detail = event.detail || {};
      var target = event.target;

      /*
        A second press while the first is still being decided.

        Control is safe from this without trying: it knows synchronously
        whether it has add-ons, so on a product with none it never cancels and
        the button stays disabled through the normal request. Variant-a cannot
        know without fetching, so it cancels first and asks after, and
        global.js re-enables the button the instant it sees the add was
        cancelled. That leaves a live button with nothing on screen yet, and on
        a product with no steps both presses used to run all the way through to
        their own rescue add, putting the line in twice.

        Dropped rather than queued. The press already in flight adds exactly
        what this one would.
      */
      var addKey = handle + ':' + (detail.variant || '') + ':' + (detail.quantity || 1);
      if (deciding[addKey]) {
        // Cancelled, not just ignored. Falling through would hand this press to
        // the normal add path and produce the very duplicate being prevented.
        event.preventDefault();
        event.stopImmediatePropagation();
        report('duplicate_add_suppressed', null, { handle: handle });
        return;
      }
      deciding[addKey] = true;
      var release = function () {
        delete deciding[addKey];
      };

      openVariantA(handle, {
        variant: detail.variant,
        quantity: detail.quantity,
        properties: detail.properties || {},
      }).then(function (opened) {
        if (opened) return;
        report('variant_declined_handback', null, { handle: handle });

        // Variant-a declined or failed. Hand the same event back to control so
        // the shopper still gets a popup rather than a cancelled add to cart.
        var again = new CustomEvent('product:before-add', {
          bubbles: true,
          cancelable: true,
          detail: detail,
        });
        again.__wbUpsellV2Passthrough = true;
        var claimed = !(target || document).dispatchEvent(again);
        if (claimed) return;

        /*
          Nobody claimed it, and the original add was cancelled before we knew
          that, so without this the button does nothing at all.

          It happens on every product with no add-ons to offer: a buckle, a
          clasp, a tool, a watch roll, a kit. Variant-a declines because it has
          no steps, control's own handler returns without cancelling because it
          has no options either, and the add we already cancelled is simply
          lost. Control never hits this because it decides whether to cancel
          before cancelling.

          The payload is what global.js sends when a product contributes no
          add-on items, which is precisely this case: the variant, the quantity
          and the form's own properties.
        */
        report('handback_unclaimed', null, { handle: handle });
        return addParentDirect(detail);
      })
      // Held until everything settles, the rescue add included, so a third
      // press cannot slip through the gap between deciding and posting.
      .then(release, release);
    },
    true
  );
})();
